// Forecast-persist command (EPIC #157, phase P5.5 — design §2.2).
//
// Persists ONE settled forecast row (the per-row body of `useForecastPersistence`'s
// save effect): emit the persist diagnostic (branch state read from the store), then POST
// the pre-computed wire snapshot. The save-EFFECT wiring (`savedRef` diff, `_pending` skip,
// baseline prime, the delete loop) stays in the hook and is retired in P6 (the design's
// debounced store-subscription persister).
//
// Moved verbatim except `store.getState()` in place of the threaded `runState`. Fire-and-
// forget POST (kept a raw `fetch` — a known `strayFetch`, ratchet-tracked → 0; services
// routing is a follow-up).
import { selectBranchRuns, selectRun } from "../lib/forecast/selectors.js";
import { STATUS } from "../lib/forecast/model.js";
import { contractKeySet } from "../lib/forecast/reducer.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { reportFailure } from "../lib/failureCounts.js";
import type { CommandDeps } from "./deps.js";

/**
 * The `!ok` saves that are not counted as `forecast.save_failed`. A 401 means the session is
 * gone and the sign-in redirect is the signal, so a count here would only echo it. A 409
 * `not_started` is the server refusing to create a row for a run that never started upstream:
 * there was nothing to save, and the failed start already told the user. Every other answer
 * (0 for a network loss, 413, 500, any other 409) is a lost write and names its status.
 */
export function isExpectedSaveMiss(status: number | undefined, error?: string): boolean {
  return status === 401 || (status === 409 && error === "not_started");
}

/**
 * Persist one forecast row. `snapshot` is the caller's `stripDebugForWire` JSON string (the
 * effect already computed it for its change-diff, so it's passed in to avoid recomputing +
 * guarantee the POST body matches what the diff recorded). Fire-and-forget.
 */
export function persistForecast(deps: CommandDeps, forecast: any, snapshot: string): void {
  const { store } = deps;
  // Diagnostic: branch run state for this root at save time, read from the store (forecast.id
  // is the root runId). nRunningNoTrace>0 flags in-flight branches with no traceId
  // (unresumable on reload); nSynthClientId flags the reforecast-rerun mismatch (a branch
  // persisted under a synthesized `…-branch-…` id — bug cad354d1 signature; should stay 0).
  // Captured in bug reports.
  const _bw = selectBranchRuns(store.getState(), forecast?.id);
  recordUiEvent({
    event: "forecast.persist",
    id: forecast.id,
    nBranches: _bw.length,
    nRunning: _bw.filter(
      (b: any) => b.status === STATUS.RUNNING || b.status === STATUS.PREPROCESSING,
    ).length,
    nRunningNoTrace: _bw.filter(
      (b: any) => (b.status === STATUS.RUNNING || b.status === STATUS.PREPROCESSING) && !b.traceId,
    ).length,
    branchIds: _bw.map((b: any) => b.runId).slice(0, 32),
    nSynthClientId: _bw.filter(
      (b: any) => typeof b.runId === "string" && b.runId.includes("-branch-"),
    ).length,
  });
  // Fire-and-forget save through the typed api seam (P5.11). `snapshot` is the caller's pre-
  // serialized wire string (the save-effect diffs it via savedRef), so parse it back to the
  // row object saveForecast expects. Guard the parse so a malformed snapshot can't throw out
  // of this best-effort path (a failed save is logged and counted, never thrown).
  let row;
  try {
    row = JSON.parse(snapshot);
  } catch {
    return; // unparseable snapshot — skip the save (best-effort)
  }
  // The save answers with the row's contract key: a backstop for a tab that missed the forecast
  // stream's late `contract_key` frame (a first forecast's contract is stored at completion).
  // Applied to the run this save was for, captured now, and only when its key differs.
  const runId: string | undefined = forecast?.id;
  void deps.services.api
    .saveForecast(row)
    .then((saved) => {
      // The typed seam never throws (W-11): a failed POST arrives as `{ok:false}`, the same
      // lost write as a rejection, so it is logged and counted the same way.
      if (!saved?.ok) {
        if (isExpectedSaveMiss(saved?.status, saved?.error)) return;
        reportFailure(
          "forecast.save_failed",
          new Error(`POST /api/forecasts answered status ${saved?.status}`),
        );
        return;
      }
      if (!runId || !saved.contractRowKey) return;
      const run: any = selectRun(store.getState(), runId);
      if (run && run.contractKey !== saved.contractRowKey)
        store.dispatch(contractKeySet(runId, saved.contractRowKey, "saved"));
    })
    .catch((err: unknown) => reportFailure("forecast.save_failed", err));
}
